import { createSlice } from '@reduxjs/toolkit';

// Generic data slice for all API data (vaccines, patients, appointments, etc.)
const createDataState = () => ({
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    status: '',
  },
  isLoading: false,
  error: null,
  lastFetched: null,
});

const initialState = {
  vaccines: createDataState(),
  patients: createDataState(),
  appointments: createDataState(),
  record: createDataState(),
  medicalStaff: createDataState(),
  centers: createDataState(),
  districts: createDataState(),
  talukas: createDataState(),
  villages: createDataState(),
  suppliers: createDataState(),
  users: createDataState(),
  stock: createDataState(),
  plan: createDataState(),
  child: createDataState(),
  vaccineDispatch: createDataState(),
  dropdowns: {
    data: {},
    isLoading: false,
    error: null,
  },
};

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    // Generic fetch actions
    fetchStart: (state, action) => {
      const { entity } = action.payload;
      if (state[entity]) {
        state[entity].isLoading = true;
        state[entity].error = null;
      }
    },
    fetchSuccess: (state, action) => {
      const { entity, items, pagination } = action.payload;
      if (state[entity]) {
        state[entity].items = items;
        state[entity].isLoading = false;
        state[entity].lastFetched = Date.now();
        if (pagination) {
          state[entity].pagination = { ...state[entity].pagination, ...pagination };
        }
      }
    },
    fetchFailure: (state, action) => {
      const { entity, error } = action.payload;
      if (state[entity]) {
        state[entity].isLoading = false;
        state[entity].error = error;
      }
    },
    fetchOneSuccess: (state, action) => {
      const { entity, item } = action.payload;
      if (state[entity]) {
        state[entity].currentItem = item;
        state[entity].isLoading = false;
      }
    },

    // CRUD actions
    createSuccess: (state, action) => {
      const { entity, item } = action.payload;
      if (state[entity]) {
        state[entity].items.unshift(item);
        state[entity].isLoading = false;
      }
    },
    updateSuccess: (state, action) => {
      const { entity, item } = action.payload;
      if (state[entity]) {
        const index = state[entity].items.findIndex(i => i.id === item.id || i._id === item._id);
        if (index !== -1) {
          state[entity].items[index] = item;
        }
        if (state[entity].currentItem?.id === item.id || state[entity].currentItem?._id === item._id) {
          state[entity].currentItem = item;
        }
        state[entity].isLoading = false;
      }
    },
    deleteSuccess: (state, action) => {
      const { entity, id } = action.payload;
      if (state[entity]) {
        state[entity].items = state[entity].items.filter(i => i.id !== id && i._id !== id);
        if (state[entity].currentItem?.id === id || state[entity].currentItem?._id === id) {
          state[entity].currentItem = null;
        }
        state[entity].isLoading = false;
      }
    },

    // Filter actions
    setFilters: (state, action) => {
      const { entity, filters } = action.payload;
      if (state[entity]) {
        state[entity].filters = { ...state[entity].filters, ...filters };
      }
    },
    setPagination: (state, action) => {
      const { entity, pagination } = action.payload;
      if (state[entity]) {
        state[entity].pagination = { ...state[entity].pagination, ...pagination };
      }
    },
    clearCurrentItem: (state, action) => {
      const { entity } = action.payload;
      if (state[entity]) {
        state[entity].currentItem = null;
      }
    },
    clearEntityError: (state, action) => {
      const { entity } = action.payload;
      if (state[entity]) {
        state[entity].error = null;
      }
    },

    // Dropdown actions
    fetchDropdownsStart: (state) => {
      state.dropdowns.isLoading = true;
      state.dropdowns.error = null;
    },
    fetchDropdownsSuccess: (state, action) => {
      state.dropdowns.data = { ...state.dropdowns.data, ...action.payload };
      state.dropdowns.isLoading = false;
    },
    fetchDropdownsFailure: (state, action) => {
      state.dropdowns.isLoading = false;
      state.dropdowns.error = action.payload;
    },

    // Reset actions
    resetEntity: (state, action) => {
      const { entity } = action.payload;
      if (state[entity]) {
        state[entity] = createDataState();
      }
    },
    resetAll: () => initialState,
  },
});

export const {
  fetchStart,
  fetchSuccess,
  fetchFailure,
  fetchOneSuccess,
  createSuccess,
  updateSuccess,
  deleteSuccess,
  setFilters,
  setPagination,
  clearCurrentItem,
  clearEntityError,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  resetEntity,
  resetAll,
} = dataSlice.actions;

// Selectors
export const selectEntity = (entity) => (state) => state.data[entity];
export const selectEntityItems = (entity) => (state) => state.data[entity]?.items || [];
export const selectEntityLoading = (entity) => (state) => state.data[entity]?.isLoading || false;
export const selectEntityError = (entity) => (state) => state.data[entity]?.error || null;
export const selectEntityPagination = (entity) => (state) => state.data[entity]?.pagination || {};
export const selectEntityFilters = (entity) => (state) => state.data[entity]?.filters || {};
export const selectCurrentItem = (entity) => (state) => state.data[entity]?.currentItem || null;
export const selectDropdowns = (state) => state.data.dropdowns.data;
export const selectDropdownsLoading = (state) => state.data.dropdowns.isLoading;

export default dataSlice.reducer;
